import { Pressable, Text, View } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";

import { Icon } from "../Icon/Icon";
import { useArmedConfirm } from "./useArmedConfirm";
import { webTapFloor } from "../theme";

// <ConfirmRemoveButton> — the two-step ✕ remove idiom (tap → "Confirm this?"
// label → second tap executes, armed state auto-reverting via
// useArmedConfirm) lifted verbatim out of ProgramExerciseEditor and
// PlanExerciseEditor, its two byte-identical copies (CN-19,
// §CONSOLIDAMENTO). Callers keep owning the `removeBtn` / `removeBtnText`
// style objects (already a single shared PlanStyles definition) and pass
// them straight through, so the visual result is unchanged.
//
// Two a11y floors are fixed HERE rather than at the callsites, which is the
// point of having one component instead of two copies:
//   - hitSlop. The shared `removeBtn` is minHeight 40, under the 44pt floor,
//     and neither original carried a hitSlop. 2px a side closes it for both
//     on native. react-native-web's Pressable ignores hitSlop entirely
//     (DS-48), so web additionally gets `minHeight: webTapFloor` appended
//     after the caller's style — a `null` array entry on native (webTapFloor
//     is undefined there) so the caller's 40 is never touched: an object
//     with an `undefined` value would clobber it via RN's style-flattening,
//     which is why this is a conditional array entry, not a merged key.
//   - the hint. At rest a screen reader announced "Remove exercise, button"
//     and, after activating, "Remove this exercise?, button", with nothing
//     saying a SECOND tap is what removes. The hint says it, and goes away
//     once armed (the label itself is the question at that point).
export function ConfirmRemoveButton({
  onRemove,
  confirmLabel,
  removeLabel,
  confirmHint,
  style,
  textStyle,
}: {
  onRemove: () => void;
  /** Shown once armed; second tap on this label executes the removal. */
  confirmLabel: string;
  /** Shown at rest; first tap arms the confirm. */
  removeLabel: string;
  /** Announced at rest: says the first tap only arms the confirm. */
  confirmHint?: string;
  style: StyleProp<ViewStyle>;
  /** Also sources the ✕ icon's size + colour (same as the label). */
  textStyle: { color: string; fontSize: number; fontWeight?: TextStyle["fontWeight"] };
}) {
  const [confirming, setConfirming] = useArmedConfirm();
  return (
    <Pressable
      accessibilityRole="button"
      {...(!confirming && confirmHint
        ? { accessibilityHint: confirmHint }
        : {})}
      hitSlop={{ top: 2, bottom: 2, left: 2, right: 2 }}
      onPress={() => {
        if (!confirming) {
          setConfirming(true);
          return;
        }
        setConfirming(false);
        onRemove();
      }}
      style={[style, webTapFloor !== undefined && { minHeight: webTapFloor }]}
    >
      {confirming ? (
        <Text style={textStyle}>{confirmLabel}</Text>
      ) : (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Icon name="close" size={textStyle.fontSize} color={textStyle.color} />
          <Text style={textStyle}>{removeLabel}</Text>
        </View>
      )}
    </Pressable>
  );
}
